/**
 * X402 Facilitator Client
 * Handles communication with the facilitator service
 */

import fetch from 'node-fetch'
import {
  FacilitatorConfig,
  VerificationRequest,
  VerificationResponse,
  SettlementRequest,
  SettlementResponse,
  X402Error,
  FacilitatorError,
  PaymentRequirement,
} from '../lib'
import { getFacilitatorBaseUrl,mapSUINetwork } from '../lib'


export class X402Client {
  private config: Required<FacilitatorConfig & { baseUrl: string }>

  constructor(config: FacilitatorConfig) {
    // Validate API key - absolutely required
    if (!config.apiKey || config.apiKey.trim() === '') {
      throw new Error('apiKey is required for X402Client configuration and cannot be empty.')
    }

    // Prevent using development/test keys
    if (config.apiKey === 'BUILDERMARE' || config.apiKey.toLowerCase().includes('test') || config.apiKey.toLowerCase().includes('dev')) {
      throw new Error('Invalid API key. Development/test keys are not allowed. Please use a valid production API key.')
    }

    this.config = {
      network: config.network ? mapSUINetwork(config.network) : 'sui:testnet',
      timeout: config.timeout || 30000,
      headers: config.headers || {},
      ...config,
      baseUrl: getFacilitatorBaseUrl() // Always use environment-based URL
    } as Required<FacilitatorConfig & { baseUrl: string }>
  }

  /**
   * Verify a payment with the facilitator
   */
  async verifyPayment(
    paymentHeader: string,
    paymentRequirements: PaymentRequirement
  ): Promise<VerificationResponse> {
    const payload: VerificationRequest = {
      paymentHeader,
      paymentRequirements
    }

    try {
      const response = await this.request('/verify', {
        method: 'POST',
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        throw new FacilitatorError(`Verification failed: ${response.statusText}`)
      }

      const result: VerificationResponse = await response.json()
      return result
    } catch (error) {
      if (error instanceof X402Error) throw error
      const message = error instanceof Error ? error.message : String(error)
      throw new FacilitatorError(`Verification request failed: ${message}`)
    }
  }

  /**
   * Settle a payment with the facilitator
   */
  async settlePayment(
    paymentHeader: string,
    paymentRequirements: PaymentRequirement
  ): Promise<SettlementResponse> {
    const payload: SettlementRequest = {
      paymentHeader,
      paymentRequirements
    }

    try {
      const response = await this.request('/settle', {
        method: 'POST',
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        throw new FacilitatorError(`Settlement failed: ${response.statusText}`)
      }

      const result: SettlementResponse = await response.json()
      return result
    } catch (error) {
      if (error instanceof X402Error) throw error
      const message = error instanceof Error ? error.message : String(error)
      throw new FacilitatorError(`Settlement request failed: ${message}`)
    }
  }

  /**
   * Make HTTP request to facilitator
   */
  private async request(path: string, options: RequestInit = {}): Promise<any> {
    const url = `${this.config.baseUrl}${path}`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.config.headers
    }
    
    // Only add API key if provided (working demo doesn't use API key)
    if (this.config.apiKey) {
      headers['X-API-Key'] = this.config.apiKey
    }

    // Merge additional headers if provided
    if (options.headers) {
      Object.assign(headers, options.headers as Record<string, string>)
    }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout)

    try {
      const fetchOptions: any = {
        method: options.method || 'GET',
        headers,
        signal: controller.signal
      }

      if (options.body) {
        fetchOptions.body = options.body
      }

      const response = await fetch(url, fetchOptions)

      clearTimeout(timeoutId)
      return response
    } catch (error) {
      clearTimeout(timeoutId)

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new X402Error(`Request timeout after ${this.config.timeout}ms`, 'TIMEOUT', 408)
        }

        if (error.message.includes('ECONNREFUSED') || error.message.includes('ENOTFOUND')) {
          throw new X402Error(`Facilitator service unreachable: ${url}`, 'NETWORK_ERROR', 503)
        }

        if (error.message.includes('ECONNRESET') || error.message.includes('EPIPE')) {
          throw new X402Error('Connection lost during request', 'CONNECTION_ERROR', 503)
        }
      }

      throw new X402Error(
        error instanceof Error ? error.message : 'Unknown network error',
        'NETWORK_ERROR',
        503
      )
    }
  }
}
